import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useIsMobile, useSelectedCompany } from '@fingo/lib/hooks';
import GET_USER from '@fingo/lib/graphql/queries/get-user';
import UploadFileInput from '@fingo/lib/components/inputs/UploadFileInput';
import UPLOAD_CERTIFICATE from '@fingo/lib/graphql/mutations/upload-certificate';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { DIGITAL_CRETIFICATE_INITIAL_VALUE } from '../../../constants/credentials';

const UploadDigitalCertificateForm = ({ credentialProviderName }) => {
  const isMobile = useIsMobile();
  const [certificate, setCertificate] = useState(DIGITAL_CRETIFICATE_INITIAL_VALUE);
  const selectedCompany = useSelectedCompany();

  const [uploadCertificate, { loading }] = useMutation(UPLOAD_CERTIFICATE, {
    variables: {
      companyId: selectedCompany.id,
      credentialProviderName,
    },
    onCompleted: () => {
      setCertificate(DIGITAL_CRETIFICATE_INITIAL_VALUE);
    },
    refetchQueries: [GET_USER],
    awaitRefetchQueries: true,
  });

  const handleCertificatePassword = useCallback(
    ({ target: { value } }) => {
      setCertificate((prevState) => ({ ...prevState, password: value }));
    },
    [certificate],
  );

  const handleCertificateFile = useCallback(
    ({ target: { files } }) => {
      setCertificate((prevState) => ({ ...prevState, file: files[0] }));
    },
    [certificate],
  );

  const handleAccept = useCallback(() => {
    uploadCertificate({
      variables: {
        certificatePassword: certificate.password,
        digitalCertificate: certificate.file,
      },
    });
  }, [certificate]);

  return (
    <Stack alignItems="center">
      <FormControl fullWidth sx={{ mb: 3 }}>
        <UploadFileInput
          onChange={handleCertificateFile}
          file={certificate.file}
          label="Sube tu certificado digital"
        />
        {certificate.file && (
          <Stack direction="row">
            <CheckCircle color="success" sx={{ mr: 1.5, fontSize: 16 }} />
            <Typography variant="caption">{certificate.file.name}</Typography>
          </Stack>
        )}
      </FormControl>
      <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
        <Typography mb={1}>Ingresa la clave del certificado</Typography>
        <TextField
          onChange={handleCertificatePassword}
          value={certificate.password}
          type="password"
          autoComplete="new-password"
        />
      </FormControl>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={handleAccept}
        disabled={loading}
        endIcon={loading && <CircularProgress size={20} />}
        id="save-certificate"
        sx={{ width: isMobile ? '100%' : '50%' }}
      >
        Guardar certificado
      </Button>
    </Stack>
  );
};

UploadDigitalCertificateForm.propTypes = {
  credentialProviderName: PropTypes.string,
};

UploadDigitalCertificateForm.defaultProps = {
  credentialProviderName: '',
};

export default UploadDigitalCertificateForm;
